import { useContext } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  IPersonKursusFilterSetting,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import { UserQueryContext } from "../../../../context/UserContext";
import AutoCompleteKursusLinje, {
  AutoCompleteKursusLinjeOnSelectedItem,
} from "../../../autocomplete/AutoCompleteKursusLinje";
import {
  DeltagerStatusId,
  PersonDtoFilterInput,
} from "../../../../interfaces/schema";
import EnumCheckBox from "../../../checkbox/EnumCheckBox";
import { f } from "../../../../util/f";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "kursus";

type UpdateEvent =
  | { type: "status"; selectedEnumKeys: Array<keyof typeof DeltagerStatusId> }
  | {
      type: "kursusnavn";
      kursusLinjeItem: AutoCompleteKursusLinjeOnSelectedItem;
    };

export default function PersonKursusFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);
  const { user, hasPermission } = useContext(UserQueryContext);

  // find active settings
  const settings: IPersonKursusFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  if (!hasPermission(user, "KursusRead")) {
    return null;
  }

  // helper
  function buildFilter(updateSettings: IPersonKursusFilterSetting) {
    return f<PersonDtoFilterInput>()
      .addFilter("kursusPerson", {
        some: {
          and: [
            updateSettings.kurser.length
              ? {
                  hold: {
                    or: updateSettings.kurser.map((kursus) => {
                      return {
                        id: {
                          eq: kursus.id,
                        },
                      };
                    }),
                  },
                }
              : {},
            updateSettings.status.length
              ? {
                  deltagerStatus: {
                    in: updateSettings.status.map(
                      (key) => DeltagerStatusId[key],
                    ),
                  },
                }
              : {},
          ],
        },
      })
      .build();
  }

  // handlers
  function updateFilterHandler(event: UpdateEvent) {
    const kursusNavneSetting: IPersonKursusFilterSetting["kurser"] =
      settings?.kurser || [];
    if (event.type === "kursusnavn") {
      if (!event.kursusLinjeItem) {
        return;
      }

      const doesFilterExists = kursusNavneSetting.some(
        (set) => set.id === event.kursusLinjeItem?.id,
      );

      if (doesFilterExists) {
        return;
      }

      kursusNavneSetting.push({
        id: event.kursusLinjeItem.id,
        name: event.kursusLinjeItem.header,
        type: event.kursusLinjeItem.href?.startsWith("linje")
          ? "Linje"
          : "Kursus",
      });
    }

    let statusSetting: IPersonKursusFilterSetting["status"] =
      settings?.status || [];

    if (event.type === "status") {
      statusSetting = event.selectedEnumKeys;
    }

    const updatedSettings: IPersonKursusFilterSetting = {
      kurser: kursusNavneSetting,
      status: statusSetting,
    };

    const canBuildFilter = updatedSettings.kurser.length;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonKursusFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: string) {
    const updatedKurser = settings.kurser.filter((kursus) => kursus.id !== id);
    const updatedStatus = updatedKurser.length ? settings.status : [];
    const updatedSettings: IPersonKursusFilterSetting = {
      kurser: updatedKurser,
      status: updatedStatus,
    };

    const canBuildFilter = updatedSettings.kurser.length;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonKursusFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    if (filterString) {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Kursus"
      value="kursus"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <h4 className="h6 mb-1 text-secondary">Kursusnavn</h4>
      <div className="c-applied-filter-group">
        {settings?.kurser.map(
          (set: IPersonKursusFilterSetting["kurser"][number]) => {
            return (
              <AppliedFilterItem
                key={set.id}
                labelPrefix={set.type}
                label={set.name}
                removeFilterHandler={() => {
                  removeFilterHandler(set.id);
                }}
              />
            );
          },
        )}
      </div>
      <AutoCompleteKursusLinje
        clearAfterSelection
        includeFirstItem={false}
        onSelectedItemChange={({ selectedItem }) => {
          updateFilterHandler({
            type: "kursusnavn",
            kursusLinjeItem: selectedItem,
          });
        }}
      />
      <h4 className="h6 mb-1 mt-3 text-secondary">Status</h4>
      <div className="l-flex-grid mt-1">
        <EnumCheckBox
          enum={DeltagerStatusId}
          state={settings?.status}
          disabled={!settings?.kurser.length}
          onChange={(selectedEnumKeys) =>
            updateFilterHandler({
              type: "status",
              selectedEnumKeys: selectedEnumKeys,
            })
          }
        />
      </div>
    </AccordionFilterItem>
  );
}
